import { Card, Select } from "@jhool-io/fe-components";
import { useQueryClient } from "@tanstack/react-query";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import { useUpdateDefaultPractice } from "../../../../hooks/mutations/user-management";
import useToast from "../../../../hooks/useToast";
import { IUser } from "../../../../utils/types/user";
import { truncateString } from "../../../../utils/helpers";

type Option = {
    label: string;
    value: string;
};

export default function General() {
    const { data: loggedInUser } = useFetchUserDetails();
    const updateDefaultPractice = useUpdateDefaultPractice();
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const practiceOptions: Option[] =
        loggedInUser?.practices?.map((p) => ({
            label: truncateString(p.practice_name, 25),
            value: p.practice_id,
        })) ?? [];

    const noneOption: Option = {
        label: "Default",
        value: "",
    };

    const allOptions = [noneOption, ...practiceOptions];

    const currentDefault = loggedInUser?.default_practice_id ?? "";

    const selectedOption =
        allOptions.find((o) => o.value === currentDefault) ?? noneOption;

    const handleDefaultPracticeChange = (val: unknown) => {
        const selected = val as Option;
        const practiceId = selected.value || null;

        const previousUser = queryClient.getQueryData<IUser>(["user-details"]);

        queryClient.setQueryData<IUser>(["user-details"], (prev) => {
            if (!prev) return prev;
            return { ...prev, default_practice_id: practiceId };
        });

        updateDefaultPractice.mutate(practiceId, {
            onSuccess(res) {
                toast({
                    mode: "success",
                    message:
                        res.message || "Default practice updated successfully",
                });
            },
            onError(err) {
                queryClient.setQueryData(["user-details"], previousUser);
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not update default practice, please try again",
                });
            },
        });
    };

    return (
        <>
            <h3 className="font-medium text-caption uppercase mb-[--spacing-items]">
                General
            </h3>
            <Card>
                <div className="p-[--padding-card-x-lg]">
                    <div className="flex flex-col gap-[--spacing-items] md:flex-row md:items-center md:justify-between md:gap-x-[--spacing-sections]">
                        <div className="md:flex-1">
                            <p className="text-base font-medium">
                                Default practice
                            </p>
                            <p className="text-textSec text-caption">
                                Choose which practice opens automatically when
                                you sign in.
                            </p>
                        </div>
                        <div className="w-full md:w-[220px] md:shrink-0">
                            <Select
                                placeholder="Select a practice"
                                options={allOptions}
                                onChange={handleDefaultPracticeChange}
                                value={selectedOption}
                                isDisabled={updateDefaultPractice.isLoading}
                            />
                        </div>
                    </div>
                </div>
            </Card>
        </>
    );
}
